import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, adminHeaders } from "@/shop/api";
import { RequireAdmin, AdminShell } from "@/pages/AdminShell";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#7ED321]";

const SettingsForm = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ payu_key: "", payu_salt: "", payu_env: "sandbox", fb_pixel_id: "", fb_test_event_code: "", cod_enabled: true, shipping_fee: 49, free_shipping_above: 500 });
  const [saving, setSaving] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: adminHeaders() }).then((r) => {
      setSettings(r.data);
      setForm((f) => ({ ...f, payu_key: r.data.payu_key, payu_env: r.data.payu_env || "sandbox", fb_pixel_id: r.data.fb_pixel_id, fb_test_event_code: r.data.fb_test_event_code || "", cod_enabled: r.data.cod_enabled, shipping_fee: r.data.shipping_fee, free_shipping_above: r.data.free_shipping_above }));
    }).catch(() => toast.error("Could not load settings — are you logged in?"));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        ...form,
        cod_enabled: form.cod_enabled,
        shipping_fee: Number(form.shipping_fee),
        free_shipping_above: Number(form.free_shipping_above),
      }, { headers: adminHeaders() });
      toast.success("Settings saved.");
      const { data } = await axios.get(`${API}/admin/settings`, { headers: adminHeaders() });
      setSettings(data);
      setForm((f) => ({ ...f, payu_salt: "" }));
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/settings/test-payu`, {}, { headers: adminHeaders() });
      setTestResult({ ok: true, msg: res.data.message });
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || "Connection test failed. Check Merchant Key & Salt.";
      setTestResult({ ok: false, msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (!settings) return <p className="text-sm text-white/40">Loading…</p>;

  const testEmail = async () => {
    setEmailTesting(true);
    setEmailResult(null);
    try {
      await axios.post(`${API}/admin/settings/test-email`, {}, { headers: adminHeaders() });
      setEmailResult({ ok: true, msg: "Test email sent to info@zentrafoods.in — check the inbox (and spam folder)." });
      toast.success("Test email sent.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Email test failed.";
      setEmailResult({ ok: false, msg });
      toast.error(msg);
    } finally {
      setEmailTesting(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-xl space-y-5" data-testid="settings-form">
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="settings-payu-card">
        <h2 className="font-display text-xl uppercase tracking-wide text-white">PayU</h2>
        <p className="mt-2 text-xs leading-relaxed text-[#A6A66A]">
          Keys from your PayU dashboard (Developer → API Keys). Test keys are pre-loaded and run
          against PayU's sandbox — swap in your live Merchant Key and Salt here when you're ready
          to accept real payments.
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">Environment</label>
            <div className="flex gap-2" data-testid="settings-payu-env">
              {[["sandbox", "Sandbox"], ["live", "Live"]].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, payu_env: val }))}
                  data-testid={`settings-payu-env-${val}`}
                  className={`rounded-full px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    form.payu_env === val
                      ? "bg-[#7ED321] text-[#0A0A0A]"
                      : "border border-white/20 text-white/60 hover:border-[#7ED321] hover:text-[#7ED321]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {form.payu_env === "live" ? (
              <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-[11px] leading-relaxed text-amber-300" data-testid="settings-payu-live-warning">
                Live mode charges real money — only switch after pasting your approved live Merchant Key and Salt from the PayU dashboard.
              </p>
            ) : (
              <p className="mt-2 text-[11px] leading-relaxed text-white/40">
                Sandbox mode runs against PayU's test page — no real money moves. Test with card 5123 4567 8901 2346 (any future expiry, CVV 123, OTP 123456) or UPI ID anything@payu.
              </p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">Merchant Key</label>
            <input
              value={form.payu_key}
              onChange={(e) => setForm((f) => ({ ...f, payu_key: e.target.value.trim() }))}
              placeholder="e.g. hdn8KB"
              data-testid="settings-payu-key"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">
              Merchant Salt {settings.has_payu_salt && <span className="text-[#7ED321]">(saved — leave blank to keep)</span>}
            </label>
            <input
              type="password"
              value={form.payu_salt}
              onChange={(e) => setForm((f) => ({ ...f, payu_salt: e.target.value.trim() }))}
              placeholder={settings.has_payu_salt ? "••••••••" : "Paste merchant salt"}
              data-testid="settings-payu-salt"
              className={inputCls}
            />
            <p className="mt-2 text-[11px] leading-relaxed text-white/40">
              The salt is stored on the server only — it's never shown back or sent to the browser.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="settings-shipping-card">
        <h2 className="font-display text-xl uppercase tracking-wide text-white">Shipping</h2>
        <p className="mt-2 text-xs leading-relaxed text-[#A6A66A]">
          Applied instantly at checkout. Set the charge to 0 for free shipping on every order.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">Flat shipping charge (₹)</label>
            <input
              type="number"
              min="0"
              value={form.shipping_fee}
              onChange={(e) => setForm((f) => ({ ...f, shipping_fee: e.target.value }))}
              data-testid="settings-shipping-fee"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">Free shipping above (₹)</label>
            <input
              type="number"
              min="0"
              value={form.free_shipping_above}
              onChange={(e) => setForm((f) => ({ ...f, free_shipping_above: e.target.value }))}
              data-testid="settings-free-shipping-above"
              className={inputCls}
            />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6">
        <h2 className="font-display text-xl uppercase tracking-wide text-white">Tracking & COD</h2>
        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">Facebook Pixel ID (optional)</label>
            <input
              value={form.fb_pixel_id}
              onChange={(e) => setForm((f) => ({ ...f, fb_pixel_id: e.target.value.trim() }))}
              placeholder="e.g. 1234567890123456"
              data-testid="settings-pixel-id"
              className={inputCls}
            />
            <p className="mt-2 text-[11px] text-white/40">Fires a Purchase event on the Thank You page once set.</p>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white/40">Meta Test Event Code (optional)</label>
            <input
              value={form.fb_test_event_code}
              onChange={(e) => setForm((f) => ({ ...f, fb_test_event_code: e.target.value.trim() }))}
              placeholder="e.g. TEST12345"
              data-testid="settings-test-event-code"
              className={inputCls}
            />
            <p className="mt-2 text-[11px] leading-relaxed text-white/40">
              Paste the code from Events Manager → Test Events to watch events arrive while testing.
              Leave empty in normal operation — remove after testing.
            </p>
          </div>
          <label className="flex cursor-pointer items-center gap-3 text-sm text-white/80" data-testid="settings-cod-toggle">
            <input
              type="checkbox"
              checked={form.cod_enabled}
              onChange={(e) => setForm((f) => ({ ...f, cod_enabled: e.target.checked }))}
              className="h-4 w-4 accent-[#7ED321]"
            />
            Show Cash on Delivery option at checkout
          </label>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="settings-email-card">
        <h2 className="font-display text-xl uppercase tracking-wide text-white">Email Notifications</h2>
        <p className="mt-2 text-xs leading-relaxed text-[#A6A66A]">
          Customers get an order confirmation and a shipped email (with tracking link) from
          Zentra &lt;info@zentrafoods.in&gt;, and you get a new-order alert in the same inbox.
          Sent via Hostinger email.
        </p>
        <button
          type="button"
          onClick={testEmail}
          disabled={emailTesting}
          data-testid="settings-test-email-button"
          className="mt-4 rounded-full border border-white/20 bg-transparent px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-[#7ED321] hover:text-[#7ED321] disabled:opacity-50"
        >
          {emailTesting ? "Sending…" : "Send Test Email"}
        </button>
        {emailResult && (
          <div
            data-testid="settings-test-email-result"
            className={`mt-3 rounded-xl border p-3 text-xs ${
              emailResult.ok
                ? "border-[#7ED321]/40 bg-[#7ED321]/10 text-[#7ED321]"
                : "border-red-500/40 bg-red-500/10 text-red-300"
            }`}
          >
            {emailResult.msg}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          data-testid="settings-save-button"
          className="rounded-full bg-[#7ED321] px-8 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
        <button
          type="button"
          onClick={testConnection}
          disabled={testing}
          data-testid="settings-test-button"
          className="rounded-full border border-white/20 bg-transparent px-6 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-[#7ED321] hover:text-[#7ED321] disabled:opacity-50"
        >
          {testing ? "Testing Connection…" : "Test PayU Connection"}
        </button>
      </div>

      {testResult && (
        <div
          data-testid="settings-test-result"
          className={`rounded-xl border p-4 text-sm ${
            testResult.ok
              ? "border-[#7ED321]/40 bg-[#7ED321]/10 text-[#7ED321]"
              : "border-red-500/40 bg-red-500/10 text-red-300"
          }`}
        >
          {testResult.msg}
        </div>
      )}
    </form>
  );
};

const AdminSettings = () => (
  <RequireAdmin>
    <AdminShell title="Settings">
      <SettingsForm />
    </AdminShell>
  </RequireAdmin>
);

export default AdminSettings;
